import http from '../http'
import { IWalletDto, IntegrationType } from './dtos'
import { mapAPIIntegrationToDto, mapIntegrationFromDto, mapWalletFromDto } from './helpers'
import {
	IAPIImport,
	IAuthImport,
	ICSVImport,
	IIntegration,
	IMerlinSyncParams,
	ISaveWalletParams,
	IScanBlockchain,
	IVezgoIntegrateParams,
	IVezgoSyncParams,
	IWallet,
	IWealthicaSyncParams,
} from './models'

const integrationsAPI = http.createClient(`${process.env.NEXT_PUBLIC_API_URL}/integrations`)
const walletsAPI = http.createClient(`${process.env.NEXT_PUBLIC_API_URL}/wallets`)
const vezgoAPI = http.createClient(`${process.env.NEXT_PUBLIC_API_URL}/integrations/vezgo`)
const merlinAPI = http.createClient(`${process.env.NEXT_PUBLIC_API_URL}/integrations/merlin`)
const wealthicaAPI = http.createClient(`${process.env.NEXT_PUBLIC_API_URL}/integrations/wealthica`)

export const getIntegrations = async () => {
	const { data } = await integrationsAPI.get<IntegrationType[]>('')
	return data.map(mapIntegrationFromDto)
}

export const getIntegrationById = async (id: IIntegration['id']) => {
	const { data } = await integrationsAPI.get<IntegrationType>(`/${id}`)
	return mapIntegrationFromDto(data)
}

export const getIntegrationStatistic = async () => {
	const { data } = await integrationsAPI.get('/statistics')
	return data
}

export const apiImport = async (params: IAPIImport | IAuthImport) => {
	const { data } = await integrationsAPI.post('', mapAPIIntegrationToDto(params))
	return data
}

export const csvImport = async (params: ICSVImport) => {
	const { data } = await integrationsAPI.post(`/${params.integration}/csv`, {
		fileName: params.file.name,
		contentType: params.file.type,
		fileUri: params.s3Url,
		type: params.type,
	})
	return data
}

// Controlled by Merlin API atm
export const scanBlockchain = async (params: IScanBlockchain) => {
	const { data } = await integrationsAPI.post('/blockchain', params)
	return data
}

export const merlinSyncIntegration = async ({ integrationId }: IMerlinSyncParams) => {
	const { data } = await integrationsAPI.post('/blockchain/sync', {
		integrationId,
	})
	return data
}

export const getSupportedBlockchains = async () => {
	const { data } = await merlinAPI.get<{ id: string; name: string }[]>('/blockchains')
	return data?.map(({ name }) => name)
}

export const stopIntegration = async (id: IIntegration['id']) => {
	const { data } = await integrationsAPI.post(`/${id}/stop`)
	return data
}

export const disconnectIntegrationById = async (id: IIntegration['id']) => {
	const { data } = await integrationsAPI.delete(`/${id}`)
	return data
}

export const disconnectAllIntegrations = async () => {
	const { data } = await integrationsAPI.delete('')
	return data
}

export const getWallets = async () => {
	const { data } = await walletsAPI.get<IWalletDto[]>('')
	return data?.map(mapWalletFromDto) || []
}

export const getWalletById = async (id: IWallet['id']) => {
	const { data } = await walletsAPI.get<IWalletDto>(`/${id}`)
	return data ? mapWalletFromDto(data) : undefined
}

export const removeWallet = async (walletId: IWallet['id']) => {
	const { data } = await walletsAPI.delete(`/${walletId}`)
	return data
}

export const createWallet = async (wallet: ISaveWalletParams) => {
	const { data } = await walletsAPI.post('', wallet)
	return data
}

export const editWallet = async ({ id, ...wallet }: Partial<ISaveWalletParams>) => {
	const { data } = await walletsAPI.put(`/${id}`, wallet)
	return data
}

/** Vezgo */
export const getVezgoToken = async () => {
	const { data } = await vezgoAPI.post<{ token: string }>('/access-token')
	return data
}

export const getVezgoSecret = async () => {
	const { data } = await vezgoAPI.get('/secret')
	return data
}

export const vezgoIntegrate = async (params: IVezgoIntegrateParams) => {
	const { data } = await vezgoAPI.post('', params)
	return data
}

export const vezgoSyncIntegration = async ({ integrationId }: IVezgoSyncParams) => {
	const { data } = await vezgoAPI.post('/sync', {
		integrationId,
	})
	return data
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const wealthicaSyncIntegrations = async ({ silent, ...params }: IWealthicaSyncParams) => {
	const { data } = await wealthicaAPI.post('', params)
	return data
}
